import { Command as CommandPrimitive } from "cmdk"
import { SearchIcon } from "lucide-react"
import type { ComponentProps } from "react"

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle
} from "~/components/ui/dialog"
import { cn } from "~/lib/utils"

function Command({
	className,
	...props
}: ComponentProps<typeof CommandPrimitive>) {
	return (
		<CommandPrimitive
			data-slot="command"
			className={cn(
				"bg-background-secondary flex h-full w-full flex-col overflow-hidden rounded-md",
				className
			)}
			style={{ pointerEvents: "all" }}
			{...props}
		/>
	)
}

function CommandDialog({
	title = "Command Palette",
	description = "Search for a command to run...",
	children,
	commandProps,
	...props
}: ComponentProps<typeof Dialog> & {
	title?: string
	description?: string
	commandProps?: Omit<ComponentProps<typeof CommandPrimitive>, "children">
}) {
	return (
		<Dialog {...props}>
			<DialogHeader className="sr-only">
				<DialogTitle>{title}</DialogTitle>
				<DialogDescription>{description}</DialogDescription>
			</DialogHeader>
			<DialogContent className="overflow-hidden">
				<div className="isolate" style={{ pointerEvents: "all" }}>
					<Command
						{...commandProps}
						className={cn(
							"**:[[cmdk-group-heading]]:text-muted-foreground **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 **:[[cmdk-input]]:h-12 **:[[cmdk-item]]:px-2 **:[[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5",
							commandProps?.className
						)}
					>
						{children}
					</Command>
				</div>
			</DialogContent>
		</Dialog>
	)
}

function CommandInput({
	className,
	...props
}: ComponentProps<typeof CommandPrimitive.Input>) {
	return (
		<div
			data-slot="command-input-wrapper"
			className="flex h-9 items-center gap-2 border-b px-3"
			style={{ pointerEvents: "all" }}
		>
			<SearchIcon className="size-4 shrink-0 opacity-50" />
			<CommandPrimitive.Input
				data-slot="command-input"
				className={cn(
					"placeholder:text-muted-foreground flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-hidden disabled:cursor-not-allowed disabled:opacity-50",
					className
				)}
				style={{ pointerEvents: "all" }}
				{...props}
			/>
		</div>
	)
}

function CommandList({
	className,
	...props
}: ComponentProps<typeof CommandPrimitive.List>) {
	return (
		<CommandPrimitive.List
			data-slot="command-list"
			className={cn(
				"max-h-75 scroll-py-1 overflow-x-hidden overflow-y-auto",
				className
			)}
			{...props}
		/>
	)
}

function CommandEmpty({
	...props
}: ComponentProps<typeof CommandPrimitive.Empty>) {
	return (
		<CommandPrimitive.Empty
			data-slot="command-empty"
			className="py-6 text-center text-sm"
			{...props}
		/>
	)
}

function CommandGroup({
	className,
	layout = "list",
	...props
}: ComponentProps<typeof CommandPrimitive.Group> & {
	layout?: "list" | "icons"
}) {
	return (
		<CommandPrimitive.Group
			data-slot="command-group"
			className={cn(
				layout === "icons" &&
					"[&_[cmdk-group-items]]:grid [&_[cmdk-group-items]]:grid-cols-6 [&_[cmdk-group-items]]:gap-1",
				"text-foreground **:[[cmdk-group-heading]]:text-muted-foreground overflow-hidden p-1 **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1.5 **:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:font-medium",
				className
			)}
			{...props}
		/>
	)
}

function CommandSeparator({
	className,
	...props
}: ComponentProps<typeof CommandPrimitive.Separator>) {
	return (
		<CommandPrimitive.Separator
			data-slot="command-separator"
			className={cn("bg-border -mx-1 h-px", className)}
			{...props}
		/>
	)
}

function CommandItem({
	className,
	variant = "default",
	...props
}: ComponentProps<typeof CommandPrimitive.Item> & {
	variant?: "default" | "icon"
}) {
	return (
		<CommandPrimitive.Item
			data-slot="command-item"
			className={cn(
				"text-text-main data-[selected=true]:bg-active data-[selected=true]:text-text-main [&_svg:not([class*='text-'])]:text-icons relative flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
				variant === "icon" &&
					"aspect-square justify-center p-0 data-[chosen=true]:ring-1 data-[chosen=true]:ring-inset data-[chosen=true]:ring-primary",
				className
			)}
			style={{ pointerEvents: "all" }}
			{...props}
		/>
	)
}

function CommandShortcut({ className, ...props }: ComponentProps<"span">) {
	return (
		<span
			data-slot="command-shortcut"
			className={cn(
				"text-text-small ml-auto text-xs tracking-widest",
				className
			)}
			{...props}
		/>
	)
}

export {
	Command,
	CommandDialog,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
	CommandShortcut
}
